/*
 * Rules of the license form and of the Delete actions that do not need React: which tier is the Unlimited one,
 * how an edit of one tier field lands in the tier list, which licenses cannot be deleted and which machines get
 * a license. Kept apart from the form so they can be tested without redux-form.
 */
import { convertFromBaseUnit, convertToBaseUnit, findUnitById } from '../../../../helpers/UnitsOfMeasurement';

/*
 * An Unlimited tier is an ordinary tier coded `unlimited` with the value -1 (Software editions spec §3). The code is
 * compared the way the server does it: trimmed, whatever the case. The server also drops any unit from it.
 */
export const UNLIMITED_TIER_CODE = 'unlimited';
export const UNLIMITED_TIER_VALUE = -1;

/*
 * The licenses Central ships with (rest-api DEFAULT_ADDONS_DATA, SYN-3384). The server refuses to delete them,
 * and the editions licenses, with a 409; the list only lets the UI say so before the request.
 */
export const PROTECTED_LICENSE_CODES = Object.freeze([
	'hasChronos',
	'hasLegalHold',
	'hasContinuousAuthentication',
	'hasMultiFactorAuthentication',
	'hasFailoverAndFailback',
	'isUpgradableToOs5',
	'hasCompute',
	'hasStorage'
]);

export const EDITIONS_SYNC_SCOPE = 'editions';

const SYNC_SCOPE_LABELS = {
	editions: 'Machines on SynetoOS 6.1 or later',
	shared: 'All machines',
	legacy: 'Machines before SynetoOS 6.1'
};

export const normalizeTierCode = (code) => {
	return typeof code === 'string' ? code.trim().toLowerCase() : '';
};

export const isUnlimitedTierCode = (code) => {
	return normalizeTierCode(code) === UNLIMITED_TIER_CODE;
};

export const isUnlimitedTier = (tier) => {
	return Boolean(tier) && isUnlimitedTierCode(tier.code);
};

export const hasUnlimitedTier = (tiers) => {
	return Array.isArray(tiers) && tiers.some(isUnlimitedTier);
};

export const buildUnlimitedTier = (uuid) => {
	return { name: 'Unlimited', code: UNLIMITED_TIER_CODE, value: UNLIMITED_TIER_VALUE, unitOfMeasurementId: null, uuid };
};

export const buildEmptyTier = (uuid) => {
	return { name: '', code: '', value: null, unitOfMeasurementId: null, uuid };
};

const parseTypedValue = (value) => {
	if (value === '' || value === null || value === undefined) {
		return null;
	}

	const number = typeof value === 'number' ? value : Number(value);

	return Number.isNaN(number) ? value : number;
};

/*
 * Returns the tier list with `field` of the tier at `index` set to `value`. Values are stored in the base unit
 * (bytes, seconds, items), so a typed value is multiplied by the factor of the tier's unit and a change of unit
 * re-expresses the stored value so the number on screen keeps meaning what it says. Coding a tier `unlimited`
 * gives it the value -1 and no unit; taking that code away empties the value so one has to be typed.
 */
export const applyTierChange = (tiers, index, field, value, units) => {
	const tier = tiers[index];

	if (!tier) {
		return tiers;
	}

	const edited = { ...tier };

	if (field === 'value') {
		const typed = parseTypedValue(value);
		edited.value = typeof typed === 'number' ? convertToBaseUnit(typed, findUnitById(units, tier.unitOfMeasurementId)) : typed;
	} else if (field === 'unitOfMeasurementId') {
		const displayed = typeof tier.value === 'number' ? convertFromBaseUnit(tier.value, findUnitById(units, tier.unitOfMeasurementId)) : tier.value;
		edited.unitOfMeasurementId = value || null;
		edited.value = typeof displayed === 'number' ? convertToBaseUnit(displayed, findUnitById(units, edited.unitOfMeasurementId)) : displayed;
	} else {
		edited[field] = value;
	}

	if (field === 'code') {
		if (isUnlimitedTierCode(value)) {
			edited.value = UNLIMITED_TIER_VALUE;
			edited.unitOfMeasurementId = null;
		} else if (isUnlimitedTierCode(tier.code)) {
			edited.value = null;
		}
	}

	return tiers.map((item, position) => { return position === index ? edited : item; });
};

/*
 * The value of a tier as it is typed: the stored base value divided by the unit's factor.
 */
export const displayTierValue = (tier, units) => {
	if (!tier || typeof tier.value !== 'number') {
		return tier?.value ?? '';
	}

	return convertFromBaseUnit(tier.value, findUnitById(units, tier.unitOfMeasurementId));
};

export const isProtectedLicense = (license) => {
	if (!license) {
		return false;
	}

	return license.syncScope === EDITIONS_SYNC_SCOPE || PROTECTED_LICENSE_CODES.includes(license.code);
};

export const protectedLicensesAmong = (licenses) => {
	return (licenses || []).filter(isProtectedLicense);
};

export const describeSyncScope = (syncScope) => {
	return SYNC_SCOPE_LABELS[syncScope] || 'Unknown';
};

/*
 * The status filter of the licenses list. Terminated licenses are hidden until asked for, so the list opens on
 * the licenses still in use.
 */
export const TERMINATED_LICENSE_STATUS = 'terminated';

// The server sends the status in lower case; compared whatever the case so either spelling counts
const isTerminatedLicense = (license) => {
	return typeof license.status === 'string' && license.status.toLowerCase() === TERMINATED_LICENSE_STATUS;
};

export const LICENSE_STATUS_FILTERS = Object.freeze([
	{ value: 'active', label: 'Active', matches: (license) => { return !isTerminatedLicense(license); } },
	{ value: 'terminated', label: 'Terminated', matches: isTerminatedLicense },
	{ value: 'all', label: 'All', matches: () => { return true; } }
]);

export const DEFAULT_LICENSE_STATUS_FILTER = 'active';

export const filterLicensesByStatus = (licenses, filterValue) => {
	const filter = LICENSE_STATUS_FILTERS.find((item) => { return item.value === filterValue; });

	return filter ? (licenses || []).filter(filter.matches) : (licenses || []);
};
